import axios from "axios";
import isNil from "lodash/isNil";
import { useAxiosHook } from "../useAxiosHook";

const ROUTE_PREFIX = "entities";

export const useEntitiesHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  const createLoanEntity = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data)) {
      console.error("Loan ID or data is missing", {
        loanId,
        data,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_entity/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Entity",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Entity created successfully",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateEntityData = async ({
    loanId,
    entityId,
    data,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(entityId) || isNil(data)) {
      console.error("Loan ID or Entity ID is missing", {
        loanId,
        entityId,
        data,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_entity/${loanId}/${entityId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Entity",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Entity updated successfully",

      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * Convenience wrapper — stamps parentEntityId from the URL and pushes the
   * new entity flat onto deal.entities[]. Equivalent to calling
   * createLoanEntity with parentEntityId + ownershipPercentInParent in the
   * body; use whichever reads cleaner at the call site.
   */
  const createNestedEntityOnParent = async ({
    loanId,
    parentEntityId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(parentEntityId) || isNil(data)) {
      console.error("Loan ID, parent Entity ID, or data is missing", {
        loanId,
        parentEntityId,
        data,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_nested_entity/${loanId}/${parentEntityId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Nested Entity",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Nested entity created successfully",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * One-shot per-deal migration from the legacy nested shape
   * (deal.borrowerEntity + entity.nestedEntities[]) to the flat
   * deal.entities[] + parentEntityId model. Idempotent — safe to call
   * defensively when loading a deal that has no flat entities but still
   * carries the legacy borrowerEntity field.
   */
  const migrateToFlatEntities = async ({
    loanId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId)) {
      console.error("Loan ID is missing");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/migrate_to_flat_entities/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Migrate To Flat Entities",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessMsg: "Entities migrated to flat model",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanEntityMember = async ({
    loanId,
    data,
    entityId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isNil(entityId)) {
      console.error("Loan ID or data is missing", {
        loanId,
        data,
        entityId,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_entity_member/${loanId}/${entityId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Entity Member",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Entity Member created successfully",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * Remove one member from entities[].entityMembers[].
   *
   * Idempotent server-side — an already-removed member returns 200 with the
   * current deal, so a double-click (or a second session that deleted it
   * first) needs no special handling. 404s only when the deal
   * (`loan_not_found`) or the entity (`entity_or_loan_not_found`) is gone.
   * Returns the full refreshed deal — replace the local one, don't merge.
   */
  const deleteEntityMember = async ({
    loanId,
    entityId,
    memberId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(entityId) || isNil(memberId)) {
      console.error("Loan ID, Entity ID, or Member ID is missing", {
        loanId,
        entityId,
        memberId,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/delete_entity_member/${loanId}/${entityId}/${memberId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Entity Member",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessMsg: "Individual removed from entity",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * Remove one entity from the flat deal.entities[] array.
   *
   * Refused with **409** `{error: "entity_has_child_entities", message,
   * childEntityIds[]}` while any other entity still names this one as its
   * `parentEntityId` — pulling a parent would orphan its subtree, and that
   * can't be repaired from the UI. Deletion is leaf-up: clear the children
   * first. The caller should PREVENT the 409 (disable the affordance on a
   * parent) and treat a received one as expected client state, so 409 is
   * suppressed from the generic error toast and left to `onFailFn`.
   *
   * Not idempotent — a second delete of the same id 404s
   * (`entity_or_loan_not_found`), because the child guard reads the entity
   * before deciding. There is no undelete; the removed object survives only
   * on the audit event.
   */
  const deleteEntity = async ({
    loanId,
    entityId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(entityId)) {
      console.error("Loan ID or Entity ID is missing", { loanId, entityId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/delete_entity/${loanId}/${entityId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Entity",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessMsg: "Entity deleted",
      suppressFailStatuses: [409],
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanEntityMember = async ({
    loanId,
    entityId,
    memberId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isNil(entityId) || isNil(memberId)) {
      console.error("Loan ID or data is missing", {
        loanId,
        entityId,
        memberId,
        data,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_entity_member/${loanId}/${entityId}/${memberId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Entity Member",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Entity Member updated successfully",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    error,
    loading,
    createLoanEntity,
    updateEntityData,
    createLoanEntityMember,
    updateLoanEntityMember,
    deleteEntity,
    deleteEntityMember,
    createNestedEntityOnParent,
    migrateToFlatEntities,
  };
};
